/**
 * A merch listing's variants as the sellers' catalogs write them and the
 * clients read them: an option's identity, whether a variant's name is a
 * size, and the option set a journey's variants make, which the purchase
 * sheets draw (`@libs/buy-on-behalf-client/options`).
 */
import { sellerLineMost } from "./vendor-checkout";

/** Identity is the item and option pair; label punctuation may occur in either. */
export const optionKeyOf = (part: string | null | undefined, name: string): string =>
  JSON.stringify([part?.trim().toLowerCase() ?? "", name.trim().toLowerCase()]);

/** A variant's name that is a size: a letter size (XS, M, XXL, 3XL) or a
 *  number (10, UK 8), whatever its case. */
const SIZE = /^(?:[2-6]?X{0,4}[SL]|M|(?:(?:UK|EU|US) ?)?\d{1,2}(?:\.5)?)$/i;

/** Whether a name is a size (`SIZE`), its letters as a button may space
 *  them ("X L", Black Lounge's) read together. */
export const isSizeName = (name: string): boolean =>
  SIZE.test(name.trim()) || (/^[A-Za-z](?: [A-Za-z])+$/.test(name.trim()) && SIZE.test(name.replace(/\s+/g, "")));

/** One ticket type, as the options choose it. */
export type OptionChoice = {
  /** The journey ticket's id: the selection's `item_id`. */
  id: string;
  /** One value per option, in the options' order. */
  options: readonly string[];
  /** On sale now. One that is not shows, disabled, in the dropdowns. */
  available: boolean;
  minQuantity: number;
  /** Its seller's limit or stock; Infinity where the seller states none. */
  maxQuantity: number;
};

/** What a product is chosen by: its options' names, each option's values
 *  in the store's order, and the ticket type each combination is. */
export type OptionSet = {
  names: readonly string[];
  /** The item each option belongs to ("T-Shirt (TOUR)", "Badge"), in the
   *  options' order; null for a listing read before items were stored. */
  parts: readonly string[] | null;
  values: readonly (readonly string[])[];
  choices: readonly OptionChoice[];
};

/** A journey as its option set reads it: its options and its ticket types. */
export type OptionJourney = {
  optionNames?: readonly string[] | null;
  optionParts?: readonly string[] | null;
  tickets: readonly {
    id: string;
    options?: readonly string[] | null;
    available?: boolean;
    minQuantity: number;
    /** Absent or null: no limit of the seller's (`sellerLineMost`). */
    maxQuantity?: number | null;
  }[];
};

const comboKey = (values: readonly string[]) => JSON.stringify(values);

/**
 * The journey's option set: null when it has no options, or when they do
 * not fit together (a ticket without one value per option, two tickets with
 * the same values), so the sheet lists the ticket types instead.
 */
export function optionSetOf(journey: OptionJourney): OptionSet | null {
  const names = journey.optionNames;
  if (!names?.length || names.some((name) => !name.trim())) return null;
  // Each option once, by its item and name: a bundle's T-shirt and hoodie
  // may both be chosen by "Size".
  const parts = journey.optionParts ?? null;
  if (parts && (parts.length !== names.length || parts.some((part) => !part.trim()))) return null;
  if (new Set(names.map((name, index) => optionKeyOf(parts?.[index], name))).size !== names.length) return null;
  const seen = new Set<string>();
  const choices: OptionChoice[] = [];
  for (const ticket of journey.tickets) {
    const options = ticket.options;
    if (!options || options.length !== names.length || options.some((value) => !value.trim())) return null;
    const key = comboKey(options);
    if (seen.has(key)) return null;
    seen.add(key);
    choices.push({
      id: ticket.id,
      options,
      available: ticket.available !== false,
      minQuantity: ticket.minQuantity,
      maxQuantity: sellerLineMost(ticket),
    });
  }
  if (!choices.some((choice) => choice.available)) return null;
  const values = names.map((_, index) => {
    const own: string[] = [];
    for (const choice of choices) if (!own.includes(choice.options[index]!)) own.push(choice.options[index]!);
    return own;
  });
  return { names, parts, values, choices };
}
